import { computed } from 'vue';

/**
 * Composable para manejar el estado de una entrada nominativa SGA.
 *
 * @param {import('vue').Ref<Object>} entradaRef - Ref al objeto entrada
 * @param {Function} emit - Función emit del componente padre
 */
export function useSgaEntrada(entradaRef, emit) {
    /**
     * Actualiza un campo de la entrada y aplica resets derivados:
     * - Si documento_tipo === 'NIF' → resetea isMinor y hasNotOwnIdentification
     * - Si isMinor === false → resetea hasNotOwnIdentification
     */
    const updateField = (field, value) => {
        const updates = { [field]: value };

        if (field === 'documento_tipo' && value === 'NIF') {
            updates.isMinor = false;
            updates.hasNotOwnIdentification = false;
        }

        if (field === 'isMinor' && value === false) {
            updates.hasNotOwnIdentification = false;
        }

        entradaRef.value = { ...entradaRef.value, ...updates };
        emit('update:entrada', entradaRef.value);
    };

    /** Computed: true si la entrada pertenece a un menor de edad */
    const esMenorDeEdad = computed(() => !!entradaRef.value.isMinor);

    /**
     * Retorna un nuevo objeto entrada con isMinor y hasNotOwnIdentification
     * reseteados cuando:
     * - sincronizar === false (desincronizar datos del comprador), o
     * - entradaObj.documento_tipo === 'NIF'
     * En caso contrario retorna el mismo objeto sin modificarlo.
     *
     * Usada por DatosCompradoresSgaComponent en sincronizarPrimeraEntrada.
     *
     * @param {Object} entradaObj - Objeto entrada de referencia
     * @param {boolean} sincronizar - Si false, siempre resetea
     * @returns {Object} Objeto nuevo con los campos reseteados, o el mismo si no aplica
     */
    const applyNifReset = (entradaObj, sincronizar = true) => {
        if (!sincronizar || entradaObj.documento_tipo === 'NIF') {
            return { ...entradaObj, isMinor: false, hasNotOwnIdentification: false };
        }
        return entradaObj;
    };

    return {
        updateField,
        esMenorDeEdad,
        applyNifReset,
    };
}
